import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeartPulse, Share2, Video } from "lucide-react";
import {
  getUserMediaConsents,
  selfGrantMediaConsent,
  revokeMediaConsent,
} from "../../api/socialClipApi";
import { CONSENT_PURPOSES } from "../../constants/legalPolicy";
import "./SelfConsentCard.css";

const SELF_PURPOSES = [
  {
    purpose: CONSENT_PURPOSES.TRAINING_ANALYSIS,
    tone: "video",
    Icon: Video,
    shortKey: "consentTrainingShort",
    shortFallback: "Video",
    labelKey: "consentTraining",
    fallback: "Training video and movement analysis",
  },
  {
    purpose: CONSENT_PURPOSES.HEART_RATE,
    tone: "heart",
    Icon: HeartPulse,
    shortKey: "consentHeartRateShort",
    shortFallback: "Heart rate",
    labelKey: "consentHeartRate",
    fallback: "Heart-rate measurement (health data)",
  },
  {
    purpose: CONSENT_PURPOSES.IMAGE_PUBLIC_USE,
    tone: "public",
    Icon: Share2,
    shortKey: "consentPublicShort",
    shortFallback: "Public",
    labelKey: "consentPublic",
    fallback: "Public or social use of image",
  },
];

function emptyConsentMap() {
  return {
    [CONSENT_PURPOSES.TRAINING_ANALYSIS]: { granted: false },
    [CONSENT_PURPOSES.HEART_RATE]: { granted: false },
    [CONSENT_PURPOSES.IMAGE_PUBLIC_USE]: { granted: false },
  };
}

export function SelfConsentCard({ userId }) {
  const { t } = useTranslation();
  const [consents, setConsents] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!userId) return;
    const data = await getUserMediaConsents(userId);
    setConsents(data || emptyConsentMap());
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      try {
        const data = await getUserMediaConsents(userId);
        if (!cancelled) setConsents(data || emptyConsentMap());
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("consentLoadFailed", "Could not load consent"));
          setConsents(emptyConsentMap());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const toggle = async (purpose, granted) => {
    if (!userId || busy || !consents) return;
    setBusy(purpose);
    setError("");
    const previous = consents;
    setConsents((prev) => ({
      ...(prev || emptyConsentMap()),
      [purpose]: { ...(prev?.[purpose] || {}), granted: !granted },
    }));
    try {
      if (granted) {
        await revokeMediaConsent({ user_id: userId, purpose });
      } else {
        await selfGrantMediaConsent({ purpose });
      }
      await load();
    } catch (err) {
      setConsents(previous);
      setError(
        err.message ||
          t(
            "consentSaveFailed",
            "Could not update consent. Under 18, a linked parent grants this in the Parent Hub. Adults need a date of birth on their profile."
          )
      );
    } finally {
      setBusy(null);
    }
  };

  if (!userId) return null;

  return (
    <section className="self-consent" aria-labelledby="self-consent-title">
      <h2 id="self-consent-title" className="self-consent__title">
        {t("consentTitle", "Recording and heart rate")}
      </h2>
      <p className="self-consent__help">
        {t(
          "consentSelfHelp",
          "Adults grant or withdraw each purpose here. Under 18, a linked parent does this in the Parent Hub. A paper form is only a fallback."
        )}
      </p>
      {error ? <p className="self-consent__error" role="alert">{error}</p> : null}
      <ul className="self-consent__list">
        {SELF_PURPOSES.map(({ purpose, tone, Icon, shortKey, shortFallback, labelKey, fallback }) => {
          const granted = Boolean(consents?.[purpose]?.granted);
          const label = t(labelKey, fallback);
          const status = granted
            ? t("parentConsentGranted", "Granted")
            : t("parentConsentNotGranted", "Not granted");
          return (
            <li
              key={purpose}
              className={`self-consent__item self-consent__item--${tone}${
                granted ? " is-granted" : ""
              }`}
            >
              <span className="self-consent__icon">
                <Icon size={20} strokeWidth={2} aria-hidden="true" />
              </span>
              <div className="self-consent__copy">
                <span className="self-consent__label">{t(shortKey, shortFallback)}</span>
                <span className="self-consent__detail">{label}</span>
                <span className={`self-consent__status${granted ? " is-on" : ""}`}>
                  <span className="self-consent__status-dot" aria-hidden="true" />
                  {status}
                </span>
              </div>
              <button
                type="button"
                className={`self-consent__btn ${
                  granted ? "self-consent__btn--withdraw" : "self-consent__btn--grant"
                }`}
                disabled={!consents || busy === purpose}
                aria-pressed={granted}
                aria-label={`${granted ? t("consentWithdraw", "Withdraw") : t("consentGrant", "I consent")}: ${label}`}
                onClick={() => toggle(purpose, granted)}
              >
                {busy === purpose
                  ? t("saving", "Saving…")
                  : granted
                    ? t("consentWithdraw", "Withdraw")
                    : t("consentGrant", "I consent")}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default SelfConsentCard;
